import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import axios from "axios";
import { useSelector } from "react-redux";
import { baseURL } from "services/API";
import { useLandlords } from "services/hooks/rentals";

const REASON_OPTIONS = [
	{ value: "PROPERTY_SOLD", label: "Property sold" },
	{ value: "OWNERSHIP_TRANSFER", label: "Ownership transfer" },
	{ value: "ADMIN_CORRECTION", label: "Admin correction" },
	{ value: "OTHER", label: "Other" },
];

const defaultEffectiveAt = () => {
	const d = new Date();
	d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
	return d.toISOString().slice(0, 16);
};

const resolveLandlordId = (property) => {
	const raw = property?.related_landlord ?? property?.landlord_id;
	if (raw == null) return null;
	return typeof raw === "object" ? raw.id : raw;
};

const landlordLabel = (landlord) =>
	landlord.business_name ||
	[landlord.related_user?.first_name, landlord.related_user?.last_name].filter(Boolean).join(" ") ||
	`Landlord #${landlord.id}`;

const ReassignLandlordForm = ({ property, onSuccess, onError, submitSignal, onSubmittingChange }) => {
	const token = useSelector((state) => state.token);
	const { landlords, isLoadingLandlords } = useLandlords({ inactivation: "active" });
	const currentLandlordId = resolveLandlordId(property);

	const [newLandlordId, setNewLandlordId] = useState("");
	const [effectiveAt, setEffectiveAt] = useState(defaultEffectiveAt);
	const [reason, setReason] = useState("PROPERTY_SOLD");
	const [notes, setNotes] = useState("");
	const [formError, setFormError] = useState("");

	const eligibleLandlords = useMemo(
		() => (landlords || []).filter((l) => l.id !== currentLandlordId),
		[landlords, currentLandlordId]
	);

	useEffect(() => {
		if (!property) return;
		setNewLandlordId("");
		setEffectiveAt(defaultEffectiveAt());
		setReason("PROPERTY_SOLD");
		setNotes("");
		setFormError("");
	}, [property?.id]);

	useEffect(() => {
		if (!submitSignal) return;

		const submit = async () => {
			if (!property?.id) return;
			if (!newLandlordId) {
				setFormError("Select the new landlord.");
				onError?.("Select the new landlord.");
				return;
			}
			setFormError("");
			onSubmittingChange?.(true);
			try {
				const effective_at =
					effectiveAt?.length === 16 ? `${effectiveAt}:00` : effectiveAt;
				const response = await axios.post(
					`${baseURL}/admin/properties/reassign-landlord`,
					{
						property_id: property.id,
						new_landlord_id: Number(newLandlordId),
						effective_at,
						reason,
						notes: notes.trim(),
					},
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				onSuccess?.(response.data?.data ?? response.data);
			} catch (err) {
				const message =
					err?.response?.data?.error ||
					err?.response?.data?.message ||
					err?.message ||
					"Failed to reassign property.";
				setFormError(message);
				onError?.(message);
			} finally {
				onSubmittingChange?.(false);
			}
		};

		submit();
	}, [submitSignal]);

	if (!property) return null;

	return (
		<Box display="flex" flexDirection="column" gap={2}>
			<Typography variant="body2" color="text.secondary">
				Tenants stay on the property; only ownership changes to the selected landlord.
			</Typography>
			{formError && <Alert severity="error">{formError}</Alert>}
			<FormControl fullWidth required>
				<InputLabel>New landlord</InputLabel>
				<Select
					label="New landlord"
					value={newLandlordId}
					onChange={(e) => setNewLandlordId(e.target.value)}
					disabled={isLoadingLandlords}
				>
					<MenuItem value="">
						<em>Select landlord</em>
					</MenuItem>
					{eligibleLandlords.map((landlord) => (
						<MenuItem key={landlord.id} value={landlord.id}>
							{landlordLabel(landlord)}
						</MenuItem>
					))}
				</Select>
				{isLoadingLandlords && (
					<Box display="flex" alignItems="center" gap={1} mt={1}>
						<CircularProgress size={16} />
						<Typography variant="caption">Loading landlords…</Typography>
					</Box>
				)}
			</FormControl>
			<TextField
				label="Effective date & time"
				type="datetime-local"
				value={effectiveAt}
				onChange={(e) => setEffectiveAt(e.target.value)}
				fullWidth
				InputLabelProps={{ shrink: true }}
				required
			/>
			<FormControl fullWidth required>
				<InputLabel>Reason</InputLabel>
				<Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
					{REASON_OPTIONS.map((opt) => (
						<MenuItem key={opt.value} value={opt.value}>
							{opt.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<TextField
				label="Notes (optional)"
				value={notes}
				onChange={(e) => setNotes(e.target.value)}
				fullWidth
				multiline
				minRows={2}
				placeholder="e.g. Sold to new owner June 2026"
			/>
		</Box>
	);
};

export default ReassignLandlordForm;
